/**
 * RemotePlayer - Visual representation of other players in the scene
 * 
 * Uses a simplified character model for remote players to prevent
 * shadow map overflow and rendering issues with multiple players.
 * 
 * Key differences from local HumanoidCharacter:
 * - NO castShadow on any meshes (prevents shadow map overflow)
 * - Fewer geometry segments for better performance
 * - Still has walk/run animations
 */

import { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';

/**
 * Validate a number is finite and not NaN
 */
const isValidNumber = (n) => typeof n === 'number' && Number.isFinite(n);

/**
 * Safely get a valid position coordinate, defaulting to 0
 */
const safeCoord = (value, defaultVal = 0) => {
  return isValidNumber(value) ? value : defaultVal;
};

// Character colors - Norse/Viking inspired
const COLORS = {
  skin: '#E8BEAC',
  hair: '#4A3728',
  tunic: '#5D4037',
  pants: '#3E2723',
  boots: '#2D1F14',
};

/**
 * Simplified character for remote players
 * - NO shadow casting (prevents shadow map overflow with multiple players)
 * - Fewer geometry segments for performance
 * - Still has walk/run/seated animations
 */
function SimplifiedRemoteCharacter({ isMoving, isRunning, isSeated, externalRotation }) {
  const groupRef = useRef();
  const bodyRef = useRef();
  const leftArmRef = useRef();
  const rightArmRef = useRef();
  const leftLegRef = useRef();
  const rightLegRef = useRef();
  const animTime = useRef(0);
  const currentRotation = useRef(externalRotation || 0);
  
  useFrame((state, delta) => {
    if (!groupRef.current) return;
    
    animTime.current += delta;
    
    // Apply external rotation with smoothing
    if (externalRotation !== null && externalRotation !== undefined && isValidNumber(externalRotation)) {
      const rotDiff = externalRotation - currentRotation.current;
      let adjustedDiff = rotDiff;
      if (adjustedDiff > Math.PI) adjustedDiff -= Math.PI * 2;
      if (adjustedDiff < -Math.PI) adjustedDiff += Math.PI * 2;
      currentRotation.current += adjustedDiff * 0.15;
      groupRef.current.rotation.y = currentRotation.current;
    }
    
    // Seated pose
    if (isSeated) {
      // Bend legs forward for sitting (positive rotation = forward)
      if (leftLegRef.current) leftLegRef.current.rotation.x = Math.PI / 2.5;
      if (rightLegRef.current) rightLegRef.current.rotation.x = Math.PI / 2.5;
      // Arms in resting position
      if (leftArmRef.current) leftArmRef.current.rotation.x = -0.2;
      if (rightArmRef.current) rightArmRef.current.rotation.x = -0.2;
      // Lower body position
      if (bodyRef.current) bodyRef.current.position.y = -0.35;
      return;
    }
    
    // Reset body position when standing
    if (bodyRef.current) bodyRef.current.position.y = 0;
    
    // Animation
    const animSpeed = isRunning ? 8 : 5;
    const swingAmount = isRunning ? 0.8 : 0.5;
    
    if (isMoving) {
      const swing = Math.sin(animTime.current * animSpeed) * swingAmount;
      if (leftLegRef.current) leftLegRef.current.rotation.x = swing;
      if (rightLegRef.current) rightLegRef.current.rotation.x = -swing;
      if (leftArmRef.current) leftArmRef.current.rotation.x = -swing * 0.7;
      if (rightArmRef.current) rightArmRef.current.rotation.x = swing * 0.7;
      if (bodyRef.current) {
        bodyRef.current.position.y = Math.abs(Math.sin(animTime.current * animSpeed * 2)) * 0.05;
      }
    } else {
      // Idle - smoothly reset limbs
      if (leftLegRef.current) leftLegRef.current.rotation.x *= 0.9;
      if (rightLegRef.current) rightLegRef.current.rotation.x *= 0.9;
      if (leftArmRef.current) leftArmRef.current.rotation.x *= 0.9;
      if (rightArmRef.current) rightArmRef.current.rotation.x *= 0.9;
    }
  });
  
  return (
    <group ref={groupRef}>
      <group ref={bodyRef}>
        {/* Head - NO castShadow */}
        <mesh position={[0, 1.6, 0]}>
          <sphereGeometry args={[0.15, 8, 8]} />
          <meshStandardMaterial color={COLORS.skin} />
        </mesh>
        {/* Hair */}
        <mesh position={[0, 1.68, -0.02]}>
          <sphereGeometry args={[0.14, 8, 8, 0, Math.PI * 2, 0, Math.PI * 0.6]} />
          <meshStandardMaterial color={COLORS.hair} />
        </mesh>
        
        {/* Torso */}
        <mesh position={[0, 1.15, 0]}>
          <boxGeometry args={[0.4, 0.5, 0.22]} />
          <meshStandardMaterial color={COLORS.tunic} />
        </mesh>
        
        {/* Left Arm */}
        <group ref={leftArmRef} position={[-0.3, 1.25, 0]}>
          <mesh position={[0, -0.3, 0]}>
            <boxGeometry args={[0.1, 0.5, 0.1]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
        </group>
        
        {/* Right Arm */}
        <group ref={rightArmRef} position={[0.3, 1.25, 0]}>
          <mesh position={[0, -0.3, 0]}>
            <boxGeometry args={[0.1, 0.5, 0.1]} />
            <meshStandardMaterial color={COLORS.skin} />
          </mesh>
        </group>
      </group>
      
      {/* Hips */}
      <mesh position={[0, 0.75, 0]}>
        <boxGeometry args={[0.35, 0.15, 0.2]} />
        <meshStandardMaterial color={COLORS.pants} />
      </mesh>
      
      {/* Left Leg */}
      <group ref={leftLegRef} position={[-0.1, 0.65, 0]}>
        <mesh position={[0, -0.35, 0]}>
          <boxGeometry args={[0.12, 0.6, 0.12]} />
          <meshStandardMaterial color={COLORS.pants} />
        </mesh>
        <mesh position={[0, -0.7, 0.03]}>
          <boxGeometry args={[0.13, 0.12, 0.2]} />
          <meshStandardMaterial color={COLORS.boots} />
        </mesh>
      </group>
      
      {/* Right Leg */}
      <group ref={rightLegRef} position={[0.1, 0.65, 0]}>
        <mesh position={[0, -0.35, 0]}>
          <boxGeometry args={[0.12, 0.6, 0.12]} />
          <meshStandardMaterial color={COLORS.pants} />
        </mesh>
        <mesh position={[0, -0.7, 0.03]}>
          <boxGeometry args={[0.13, 0.12, 0.2]} />
          <meshStandardMaterial color={COLORS.boots} />
        </mesh>
      </group>
    </group>
  );
}

/**
 * Single remote player component
 * 
 * Uses SimplifiedRemoteCharacter (no shadows) instead of full HumanoidCharacter
 * to prevent shadow map overflow when multiple players are in the same scene.
 */
export default function RemotePlayer({ 
  playerId,
  displayName,
  username,
  isVerified,
  position,
  rotation,
  movementState,
  seatedZoneId,
}) {
  const groupRef = useRef();
  
  // Safely extract position with validation (prevent NaN from crashing Three.js)
  const safeX = safeCoord(position?.x, 0);
  const safeZ = safeCoord(position?.z, 0);
  const safeRotation = safeCoord(rotation, 0);
  
  // Determine animation state from movementState
  const isSeated = movementState === 'seated' || !!seatedZoneId;
  const isMoving = !isSeated && (movementState === 'walking' || movementState === 'running');
  const isRunning = movementState === 'running';
  
  // useMemo must be called before any early return to satisfy Rules of Hooks
  const movementDirection = useMemo(() => {
    if (!isMoving) return { x: 0, z: 0 };
    return {
      x: Math.sin(safeRotation),
      z: Math.cos(safeRotation),
    };
  }, [safeRotation, isMoving]);
  
  // Safety check: if position is completely invalid, don't render
  if (!position || (position.x === undefined && position.y === undefined && position.z === undefined)) {
    return null;
  }
  
  // Safe display name with fallback
  const safeDisplayName = displayName || username || 'Traveler';
  
  return (
    <group ref={groupRef} position={[safeX, 0, safeZ]}>
      {/* Simplified character (no shadow casting) */}
      <SimplifiedRemoteCharacter
        isMoving={isMoving}
        isRunning={isRunning}
        isSeated={isSeated}
        externalRotation={safeRotation}
      />
      
      {/* Name tag */}
      <group position={[0, 2.3, 0]}>
        {/* Background plate */}
        <mesh position={[0, 0, -0.01]}>
          <planeGeometry args={[safeDisplayName.length * 0.12 + 0.4, 0.35]} />
          <meshBasicMaterial color="#000000" transparent opacity={0.6} />
        </mesh>
        
        {/* Name text */}
        <Text
          position={[0, 0, 0]}
          fontSize={0.18}
          color="#FFFFFF"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="#000000"
        >
          {safeDisplayName}
        </Text>
        
        {/* Verified badge */}
        {isVerified && (
          <mesh position={[safeDisplayName.length * 0.06 + 0.25, 0, 0]}>
            <circleGeometry args={[0.08, 16]} />
            <meshBasicMaterial color="#3B82F6" />
          </mesh>
        )}
      </group>
    </group>
  );
}

/**
 * Container for all remote players
 * Filters invalid players and renders the list
 */
export function RemotePlayersContainer({ remotePlayers = [] }) {
  // Filter out players with invalid/missing data
  const validPlayers = useMemo(() => {
    return remotePlayers.filter(player => {
      if (!player.playerId) return false;
      
      const pos = player.currentPosition || player.position;
      if (!pos) return false;
      
      // Check for valid coordinates
      const x = pos.x;
      const y = pos.y;
      const z = pos.z;
      
      if (!isValidNumber(x) || !isValidNumber(y) || !isValidNumber(z)) {
        return false;
      }
      
      return true;
    });
  }, [remotePlayers]);
  
  return (
    <group>
      {validPlayers.map(player => (
        <RemotePlayer
          key={player.playerId}
          playerId={player.playerId}
          displayName={player.displayName}
          username={player.username}
          isVerified={player.isVerified}
          position={player.currentPosition || player.position}
          rotation={player.currentRotation ?? player.rotation}
          movementState={player.movementState}
          seatedZoneId={player.seatedZoneId}
        />
      ))}
    </group>
  );
}
